import { 
  collection, 
  query, 
  where, 
  getDocs, 
  orderBy, 
  limit, 
  startAfter, 
  QueryDocumentSnapshot, 
  DocumentData, 
  getCountFromServer, 
  doc, 
  getDoc, 
  updateDoc 
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from './firebase';
import { User } from '../types';

// Pagination result type
export interface PaginatedAdminUsersResult {
  users: User[];
  lastDoc: QueryDocumentSnapshot<DocumentData> | null;
  hasMore: boolean;
  total?: number;
}

// Search result type
export interface SearchAdminUsersResult {
  users: User[];
  total: number;
}

export interface CreateAdminUserResult {
  success: boolean;
  uid: string;
  action: 'created' | 'upserted';
  invitationSent: boolean;
  message: string;
}

/**
 * Helper function to process admin user data consistently
 */
const processAdminUserData = (doc: QueryDocumentSnapshot<DocumentData>): User => {
  const data = doc.data();
  
  // Helper function to convert Firestore timestamp to Date
  const convertTimestamp = (timestamp: any): Date => {
    if (!timestamp) {
      return new Date();
    }
    
    // Handle Firestore Timestamp objects
    if (timestamp && typeof timestamp === 'object' && timestamp.toDate && typeof timestamp.toDate === 'function') {
      return timestamp.toDate();
    }
    
    // Handle regular Date objects
    if (timestamp instanceof Date) {
      return timestamp;
    }
    
    // Handle string dates
    if (typeof timestamp === 'string') {
      const date = new Date(timestamp);
      return isNaN(date.getTime()) ? new Date() : date;
    }
    
    // Handle number timestamps
    if (typeof timestamp === 'number') {
      return new Date(timestamp);
    }
    
    // Fallback to current date
    return new Date();
  };
  
  return {
    id: doc.id,
    email: data.email || '',
    name: data.name || '',
    role: data.role || 'viewer',
    avatar: data.avatar || undefined,
    lastLogin: data.lastLogin ? convertTimestamp(data.lastLogin) : undefined,
    createdAt: convertTimestamp(data.createdAt),
    isActive: data.isActive !== undefined ? data.isActive : true,
  } as User;
};

/**
 * Get the total count of admin users
 */
export const getAdminUserCount = async (): Promise<number> => {
  try {
    console.log('getAdminUserCount called');
    
    const adminUsersCollection = collection(db, 'adminUsers');
    const countQuery = query(adminUsersCollection);
    
    const snapshot = await getCountFromServer(countQuery);
    const count = snapshot.data().count;
    console.log('Admin user count result:', count);
    
    return count;
  } catch (error) {
    console.error('Error getting admin user count:', error);
    throw new Error('Failed to get admin user count');
  }
};

/**
 * Fetch admin users with pagination support
 */
export const fetchAdminUsersPaginated = async (
  pageSize: number = 20,
  lastDoc: QueryDocumentSnapshot<DocumentData> | null = null
): Promise<PaginatedAdminUsersResult> => {
  try {
    console.log('fetchAdminUsersPaginated called with:', { pageSize, lastDoc: !!lastDoc });
    
    const adminUsersCollection = collection(db, 'adminUsers');
    
    // Build base query - use __name__ (document ID) for consistent ordering
    let adminUserQuery = query(
      adminUsersCollection,
      orderBy('__name__')
    );
    
    // Add pagination
    if (lastDoc) {
      adminUserQuery = query(adminUserQuery, startAfter(lastDoc));
    }
    
    adminUserQuery = query(adminUserQuery, limit(pageSize));
    
    console.log('Executing Firestore query...');
    const snapshot = await getDocs(adminUserQuery);
    console.log('Firestore query result:', { docsCount: snapshot.docs.length, empty: snapshot.empty });
    
    const users = snapshot.docs.map(processAdminUserData);
    console.log('Processed admin users:', users.length);
    
    const hasMore = snapshot.docs.length === pageSize;
    const lastDocument = snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] : null;
    
    return {
      users: users,
      lastDoc: lastDocument,
      hasMore,
    };
  } catch (error) {
    console.error('Error fetching admin users:', error);
    throw new Error('Failed to fetch admin users');
  }
};

/**
 * Search admin users by name or email
 */
export const searchAdminUsers = async (
  searchQuery: string
): Promise<SearchAdminUsersResult> => {
  try {
    const adminUsersCollection = collection(db, 'adminUsers');
    const adminUserQuery = query(adminUsersCollection, orderBy('name'));
    
    const snapshot = await getDocs(adminUserQuery);
    const allUsers = snapshot.docs.map(processAdminUserData);
    
    // Filter by search query
    const searchLower = searchQuery.toLowerCase();
    const filteredUsers = allUsers.filter(user => {
      // Search by name and email
      return user.name.toLowerCase().includes(searchLower) ||
             user.email.toLowerCase().includes(searchLower);
    });
    
    return {
      users: filteredUsers,
      total: filteredUsers.length
    };
  } catch (error) {
    console.error('Error searching admin users:', error);
    throw new Error('Failed to search admin users');
  }
};

/**
 * Fetch a single admin user by ID
 */
export const fetchAdminUserById = async (userId: string): Promise<User | null> => {
  try {
    const userDoc = doc(db, 'adminUsers', userId);
    const snapshot = await getDoc(userDoc);
    
    if (!snapshot.exists()) {
      return null;
    }
    
    return processAdminUserData(snapshot);
  } catch (error) {
    console.error('Error fetching admin user:', error);
    throw new Error('Failed to fetch admin user');
  }
};

/**
 * Update admin user information
 */
export const updateAdminUser = async (userId: string, userData: Partial<User>): Promise<void> => {
  try {
    console.log('Updating admin user:', userId, userData);
    
    // Prepare the data for Firestore update
    const updateData: any = {};
    
    if (userData.name !== undefined) {
      updateData.name = userData.name;
    }
    
    if (userData.email !== undefined) {
      updateData.email = userData.email;
    }
    
    if (userData.role !== undefined) {
      updateData.role = userData.role;
    }
    
    if (userData.isActive !== undefined) {
      updateData.isActive = userData.isActive;
    }
    
    if (userData.avatar !== undefined) {
      updateData.avatar = userData.avatar;
    }
    
    if (userData.lastLogin !== undefined) {
      updateData.lastLogin = userData.lastLogin;
    }
    
    // Add updatedAt timestamp
    updateData.updatedAt = new Date();
    
    const userDoc = doc(db, 'adminUsers', userId);
    await updateDoc(userDoc, updateData);
    
    console.log('Admin user updated successfully in Firestore');
  } catch (error) {
    console.error('Error updating admin user:', error);
    throw new Error('Failed to update admin user');
  }
};

/**
 * Create a new admin user via Firebase Function
 */
export const createAdminUser = async (userData: {
  name: string;
  email: string;
  role: string;
  isActive: boolean;
}): Promise<CreateAdminUserResult> => {
  try {
    console.log('Creating admin user:', userData);
    
    const createUserFunction = httpsCallable(functions, 'createAdminUser');
    const result = await createUserFunction(userData);
    
    console.log('Admin user created successfully:', result.data);
    return result.data as CreateAdminUserResult;
    
  } catch (error) {
    console.error('Error creating admin user:', error);
    throw error;
  }
}; 
